import { useTranslation } from 'react-i18next'
import type { TicketCounts } from '@/api/ticketsApi'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface Kpi {
  label: string
  value: number | undefined
  /** No analytics endpoint in the MVP: shown as "—" with an explanation, never a made-up number. */
  analyticsOnly?: boolean
  danger?: boolean
}

/** The queue's numbers at a glance: five cards, the critical count in the P1 colour when there are any. */
export function KpiStrip({ counts }: { counts: TicketCounts | null | undefined }) {
  const { t } = useTranslation()
  const kpis: Kpi[] = [
    { label: t('queue.kpi.open'), value: counts?.allOpen },
    { label: t('queue.kpi.critical'), value: counts?.critical, danger: true },
    { label: t('queue.kpi.slaMet'), value: undefined, analyticsOnly: true },
    { label: t('queue.kpi.medianFirstResponse'), value: undefined, analyticsOnly: true },
    { label: t('queue.kpi.escalated'), value: counts?.escalated },
  ]
  return (
    <TooltipProvider delayDuration={150}>
      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" aria-label={t('queue.kpi.open')}>
        {kpis.map((kpi) => {
          const alarm = kpi.danger && !!kpi.value
          const value = (
            <span
              className={cn(
                'text-kpi font-semibold tabular-nums',
                kpi.analyticsOnly || kpi.value === undefined ? 'text-ink-placeholder' : alarm ? 'text-priority-p1' : 'text-ink',
              )}
            >
              {kpi.value ?? '—'}
            </span>
          )
          return (
            <div
              key={kpi.label}
              className={cn(
                'flex flex-col gap-1 rounded-lg border bg-card px-4 py-3.5',
                alarm ? 'border-priority-p1/30 bg-priority-p1-bg/40' : 'border-line',
              )}
            >
              <span className="text-label text-ink-muted">{kpi.label}</span>
              {kpi.analyticsOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="w-fit cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${kpi.label}: ${t('queue.kpi.analyticsOnly')}`}>
                      {value}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t('queue.kpi.analyticsOnly')}</TooltipContent>
                </Tooltip>
              ) : (
                value
              )}
            </div>
          )
        })}
      </section>
    </TooltipProvider>
  )
}
